import { Button, Grid, Stack, Typography } from '@mui/material'
import { getCabinResource } from 'components/Appointment/util'
import { useAppointmentTypeTranslation } from 'components/Appointment/util/useAppointmentTypeTranslation'
import Lottie from 'lottie-react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router'
import { AppointmentType, EventType, Route, RouteParameters } from '../../../../constants'
import { useAppointmentsService } from '../../../../hooks/services'
import { useReservation } from '../../../../hooks/useReservation'
import {
  appointmentTypeToDisplayText,
  hydratePathname,
  isAwaitingProviderMatching,
  isPooledSchedulingInScope,
  toLongTimeZonedDateText,
  toShortTimeZonedDateText,
} from '../../../../util'
import animationData from '../../../Appointment/assets/lottie/success.json'
import { getSpecialistResource } from '../../../Appointment/util'
import { Issues } from '../../../Issues'
import { useResources } from '../../../schedule/Schedule/useResources'
import { Loading } from '../../Common/Loading'

const Success = () => {
  const { reservation, isLoading: isLoadingReservation } = useReservation()
  const followsConvertedAppointment = isPooledSchedulingInScope({
    appointmentType: reservation?.appointment_type,
    country: reservation?.country,
    journey: reservation?.journey,
  })
  const { data: resources, isLoading: isLoadingResources } = useResources(
    { resourceIds: reservation?.resource_ids },
    { enabled: Boolean(reservation) && !followsConvertedAppointment }
  )
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { getAppointment, isReady } = useAppointmentsService()

  const appointmentId = reservation?.appointment_id

  const {
    data: convertedAppointment,
    isIdle: isConvertedAppointmentIdle,
    isLoading: isLoadingConvertedAppointment,
  } = useQuery(['useConvertedAppointment', appointmentId], () => getAppointment({ id: appointmentId }), {
    enabled: followsConvertedAppointment && Boolean(appointmentId) && isReady,
    retry: false,
  })

  const displayedResources = followsConvertedAppointment ? convertedAppointment?.resources : resources?.data
  const isPooled = Boolean(convertedAppointment && isAwaitingProviderMatching(convertedAppointment))
  const shopId = displayedResources?.find((r) => r.shop_id)?.shop_id

  const appointmentParams = {
    appointmentType: appointmentTypeToDisplayText[reservation?.appointment_type ?? AppointmentType.HEARING_AID_EVALUATION],
    fitterName: getSpecialistResource(displayedResources)?.name,
    cabinName: getCabinResource(displayedResources)?.name,
    shopId,
    hideProviderName: isPooled,
  }

  const { getAppointmentTypeTranslation } = useAppointmentTypeTranslation(appointmentParams)

  const isWaitingForConvertedAppointment =
    followsConvertedAppointment && Boolean(appointmentId) && (isConvertedAppointmentIdle || isLoadingConvertedAppointment)

  if (isLoadingReservation || isLoadingResources || isWaitingForConvertedAppointment) {
    return <Loading />
  }

  if (!reservation) {
    return <Issues eventType={EventType.RESERVATION_PAGE_MISSING_PARAMETERS} summary={{ 'reservation.error': ['reservation.notFound'] }} />
  }

  const dateText = toLongTimeZonedDateText(reservation.start_date, reservation.timezone)
  const customerTime = toShortTimeZonedDateText(reservation.start_date, reservation.timezone)

  const onCancel = () => {
    const pathname = hydratePathname(Route.APPOINTMENT_CANCELLATION, {
      [RouteParameters.COUNTRY]: reservation.country,
      [RouteParameters.APPOINTMENT_ID]: reservation.appointment_id,
    })
    navigate(pathname)
  }

  return (
    <Grid container textAlign={'center'} spacing={1}>
      <Grid item xs={12}>
        <Lottie animationData={animationData} loop={false} style={{ height: '12rem' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h1" p={1}>
          {t('pages.appointment.schedule.reservationScheduled')}
        </Typography>
        <Typography variant="body1">{getAppointmentTypeTranslation()}</Typography>
        <Typography variant="body1">{dateText}</Typography>
        <Typography variant="body1">
          {customerTime} ({t('common.label.customerTime')})
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack direction={'row'} spacing={2} justifyContent={'center'} alignItems={'center'}>
          <Button onClick={onCancel} variant="outlined">
            {t('common.label.cancelAppointment')}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  )
}

export { Success as ReservationConvertSuccess }
